import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface ManagedDevice {
  id: string;
  user_id: string;
  device_name: string;
  device_type: 'printer' | 'scanner' | 'other';
  status: 'connected' | 'disconnected';
  last_connected: string | null;
  metadata: {
    connectionType: 'bluetooth' | 'wifi';
    bleDeviceId?: string;
    gattServices?: string[];
    ip?: string;
    port?: number;
    manufacturer?: string;
  } | null;
  created_at: string;
}

const BLE_OPTIONAL_SERVICES: string[] = [
  '0000ff00-0000-1000-8000-00805f9b34fb',
  '0000ffe0-0000-1000-8000-00805f9b34fb',
  '6e400001-b5a3-f393-e0a9-e50e24dcca9e',
  '00001800-0000-1000-8000-00805f9b34fb',
  '00001801-0000-1000-8000-00805f9b34fb',
  '0000180a-0000-1000-8000-00805f9b34fb', // Device Information Service
];

const REQUEST_TIMEOUT_MS = 45_000;

function isBleAvailable(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.bluetooth;
}

export function useDeviceManager() {
  const { user } = useAuth();
  const userId = user?.id;

  const [devices, setDevices] = useState<ManagedDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bleDevicesRef = useRef<Map<string, BluetoothDevice>>(new Map());

  const loadDevices = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('connected_devices')
        .select('*')
        .eq('user_id', userId)
        .order('last_connected', { ascending: false });
      if (err) throw err;
      setDevices((data || []) as ManagedDevice[]);
    } catch {
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const addDevice = useCallback(
    async (
      name: string,
      type: 'printer' | 'scanner' | 'other',
      connectionType: 'bluetooth' | 'wifi',
      metadata: Record<string, unknown>,
    ): Promise<ManagedDevice | null> => {
      if (!userId) return null;
      setError(null);
      try {
        const { data, error: err } = await supabase
          .from('connected_devices')
          .insert({
            user_id: userId,
            device_name: name,
            device_type: type,
            status: 'connected',
            last_connected: new Date().toISOString(),
            metadata: { connectionType, ...metadata },
          })
          .select()
          .single();
        if (err) throw err;
        await loadDevices();
        return data as ManagedDevice;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save device');
        return null;
      }
    },
    [userId, loadDevices],
  );

  const removeDevice = useCallback(
    async (id: string) => {
      if (!userId) return;
      setError(null);
      try {
        const device = devices.find((d) => d.id === id);
        if (device?.metadata?.connectionType === 'bluetooth' && device.metadata.bleDeviceId) {
          const bleDevice = bleDevicesRef.current.get(device.metadata.bleDeviceId);
          if (bleDevice?.gatt?.connected) {
            bleDevice.gatt.disconnect();
          }
          bleDevicesRef.current.delete(device.metadata.bleDeviceId);
        }
        const { error: err } = await supabase
          .from('connected_devices')
          .delete()
          .eq('id', id)
          .eq('user_id', userId);
        if (err) throw err;
        await loadDevices();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to remove device');
      }
    },
    [userId, devices, loadDevices],
  );

  const updateDeviceStatus = useCallback(
    async (id: string, status: 'connected' | 'disconnected') => {
      if (!userId) return;
      try {
        await supabase
          .from('connected_devices')
          .update({
            status,
            last_connected: status === 'connected' ? new Date().toISOString() : undefined,
          })
          .eq('id', id)
          .eq('user_id', userId);
        setDevices((prev) =>
          prev.map((d) => (d.id === id ? { ...d, status } : d)),
        );
      } catch {
        // silent
      }
    },
    [userId],
  );

  const scanBluetooth = useCallback(
    async (deviceType: 'printer' | 'scanner' | 'other' = 'printer') => {
      if (!isBleAvailable()) {
        setError('Web Bluetooth is not available. Use Chrome on HTTPS.');
        return null;
      }
      setScanning(true);
      setError(null);

      try {
        const requestPromise = navigator.bluetooth.requestDevice({
          acceptAllDevices: true,
          optionalServices: BLE_OPTIONAL_SERVICES,
        });

        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(
            () => reject(new Error('Bluetooth scan timed out. Ensure the device is powered on and in pairing mode.')),
            REQUEST_TIMEOUT_MS,
          ),
        );

        const bleDevice = await Promise.race([requestPromise, timeoutPromise]);

        const gatt = bleDevice.gatt;
        if (!gatt) throw new Error('GATT not available on this device.');

        if (gatt.connected) {
          try { gatt.disconnect(); } catch { /* ignore */ }
        }
        await new Promise((r) => setTimeout(r, 300));
        await gatt.connect();

        let serviceUuids: string[] = [];
        try {
          const services = await gatt.getPrimaryServices();
          serviceUuids = services.map((s) => s.uuid);
        } catch { /* some devices don't expose services */ }

        bleDevicesRef.current.set(bleDevice.id, bleDevice);

        const existing = devices.find(
          (d) => d.metadata?.bleDeviceId === bleDevice.id,
        );

        if (existing) {
          await updateDeviceStatus(existing.id, 'connected');
          setScanning(false);
          return existing;
        }

        const saved = await addDevice(
          bleDevice.name || 'Unknown Bluetooth Device',
          deviceType,
          'bluetooth',
          {
            bleDeviceId: bleDevice.id,
            gattServices: serviceUuids,
          },
        );

        setScanning(false);
        return saved;
      } catch (err) {
        const msg =
          err instanceof DOMException && err.name === 'NotFoundError'
            ? 'No device selected.'
            : err instanceof Error
              ? err.message
              : 'Bluetooth scan failed';
        setError(msg);
        setScanning(false);
        return null;
      }
    },
    [devices, addDevice, updateDeviceStatus],
  );

  const reconnectBluetooth = useCallback(
    async (managedDevice: ManagedDevice) => {
      if (!isBleAvailable() || !managedDevice.metadata?.bleDeviceId) {
        setError('Cannot reconnect: Bluetooth not available or device ID missing.');
        return false;
      }
      setError(null);

      const getDevices = (navigator.bluetooth as { getDevices?: () => Promise<BluetoothDevice[]> }).getDevices;
      if (typeof getDevices !== 'function') {
        setError('Browser does not support cached Bluetooth reconnect. Click "Scan Bluetooth" instead.');
        return false;
      }

      try {
        const cachedDevices = await getDevices();
        const bleDevice = cachedDevices.find((d) => d.id === managedDevice.metadata?.bleDeviceId);
        if (!bleDevice) {
          setError('Device not found in browser cache. Click "Scan Bluetooth" to re-pair.');
          return false;
        }

        const gatt = bleDevice.gatt;
        if (!gatt) throw new Error('GATT not available.');

        if (gatt.connected) {
          try { gatt.disconnect(); } catch { /* ignore */ }
        }
        await new Promise((r) => setTimeout(r, 300));
        await gatt.connect();

        bleDevicesRef.current.set(bleDevice.id, bleDevice);
        await updateDeviceStatus(managedDevice.id, 'connected');
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Reconnect failed');
        return false;
      }
    },
    [updateDeviceStatus],
  );

  const disconnectBluetooth = useCallback(
    async (managedDevice: ManagedDevice) => {
      if (!managedDevice.metadata?.bleDeviceId) return;
      const bleDevice = bleDevicesRef.current.get(managedDevice.metadata.bleDeviceId);
      if (bleDevice?.gatt?.connected) {
        bleDevice.gatt.disconnect();
      }
      await updateDeviceStatus(managedDevice.id, 'disconnected');
    },
    [updateDeviceStatus],
  );

  const isBluetoothConnected = useCallback(
    (managedDevice: ManagedDevice): boolean => {
      if (managedDevice.metadata?.connectionType !== 'bluetooth') return false;
      if (!managedDevice.metadata.bleDeviceId) return false;
      const bleDevice = bleDevicesRef.current.get(managedDevice.metadata.bleDeviceId);
      return bleDevice?.gatt?.connected === true;
    },
    [],
  );

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  return {
    devices,
    loading,
    scanning,
    error,
    bleAvailable: isBleAvailable(),
    loadDevices,
    addDevice,
    removeDevice,
    updateDeviceStatus,
    scanBluetooth,
    reconnectBluetooth,
    disconnectBluetooth,
    isBluetoothConnected,
    clearError: () => setError(null),
  };
}
